import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { Accordion, Badge } from "react-bootstrap";

import { ReactComponent as SortIcon } from "assets/images/icons/SortDown.svg";
import { FilterConfig } from "types/Common";
import MultiSelectFilter from "./MultiSelectFilter";
import FreightModeFilter from "./FreightModeFilter";
import DateFilter from "./DateFilter";
import SearchableTextFilter from "./SearchableTextFilter";
import AccordionFilter from "./AccordionFilter";
import TextFilter from "./TextFilter";

interface FilterAccordionsProps {
  config: FilterConfig[];
  currentFilters: Record<string, any>;
  setFilters: Dispatch<SetStateAction<Record<string, any> | undefined>> | Function;
  eventContext: string;
}

function FilterAccordions({ config, currentFilters, setFilters, eventContext }: FilterAccordionsProps) {
  const [activeAccordion, setActiveAccordion] = useState<string | undefined>(undefined);

  const isFilterSelected = (filterValue: string | object | Array<any> | undefined) => {
    if ((Array.isArray(filterValue) && filterValue.length) || (!Array.isArray(filterValue) && filterValue)) {
      return true;
    }
    return false;
  };

  const renderFilterComponent = useCallback(
    (filter: FilterConfig) => {
      switch (filter.filterType) {
        case "multiselect":
          return (
            <MultiSelectFilter
              currentFilters={currentFilters}
              setFilters={setFilters}
              options={filter.options}
              filterKey={filter.key}
              searchable={filter.isSearchable}
              eventContext={eventContext}
            />
          );
        case "freight_mode":
          return (
            <FreightModeFilter
              currentFilters={currentFilters}
              setFilters={setFilters}
              filterKey={filter.key}
              eventContext={eventContext}
              filterValueKey={filter.filterValueKey}
            />
          );
        case "date":
          return (
            <DateFilter
              currentFilters={currentFilters}
              setFilters={setFilters}
              filterKey={filter.key}
              eventContext={eventContext}
            />
          );
        case "text":
          return filter.options ? (
            <SearchableTextFilter
              currentFilters={currentFilters}
              setFilters={setFilters}
              filterKey={filter.key}
              options={filter.options}
            />
          ) : (
            <TextFilter currentFilters={currentFilters} setFilters={setFilters} filterKey={filter.key} />
          );
        case "accordion":
          return (
            <AccordionFilter
              renderFilterComponent={renderFilterComponent}
              nestedFilters={filter.nestedFilters}
              isFilterSelected={isFilterSelected}
              currentFilters={currentFilters}
            />
          );
        default:
          return null;
      }
    },
    [currentFilters],
  );

  return (
    <Accordion onSelect={(eventKey) => setActiveAccordion(eventKey as string)} activeKey={activeAccordion}>
      {config.map((filter) => {
        let activeAccordionFilter;
        if (filter.filterType === "accordion") {
          activeAccordionFilter = filter.nestedFilters.filter((accFilter: any) => {
            const filterKey = accFilter.key;
            const filterValue = currentFilters[filterKey];
            return (
              filterValue !== null &&
              filterValue !== "" &&
              (typeof filterValue === "string" || typeof filterValue === "object")
            );
          }).length;
        }
        return (
          <div key={filter.key} className="mb-16">
            <Accordion.Toggle as="h6" eventKey={filter.key}>
              <div className="d-flex align-items-center">
                <p className="mb-0 font-weight-bold">{filter.label}</p>
                <SortIcon
                  className="ml-1"
                  fill="#000"
                  width={8}
                  height={7}
                  transform={`rotate(${activeAccordion === filter.key ? 180 : 0})`}
                />
                {activeAccordionFilter > 0 && filter.key !== activeAccordion && (
                  <div className="ml-2">
                    <Badge variant="primary" pill>
                      {activeAccordionFilter}
                    </Badge>
                  </div>
                )}
                {isFilterSelected(currentFilters[filter.key]) && (
                  <div className="ml-2">
                    <Badge variant="primary" pill>
                      {Array.isArray(currentFilters[filter.key]) ? currentFilters[filter.key]?.length : 1}
                    </Badge>
                  </div>
                )}
              </div>
            </Accordion.Toggle>
            <Accordion.Collapse eventKey={filter.key}>
              <div className="mt-3">{renderFilterComponent(filter)}</div>
            </Accordion.Collapse>
          </div>
        );
      })}
    </Accordion>
  );
}

export default FilterAccordions;
